import React, {useState, useEffect, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Modal,
  Stack,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import {CircleMinus} from 'lucide-react';
import {useAuthFrontendApis} from '@propelauth/frontend-apis-react';
import {useSelector} from 'react-redux';
import {useTheme} from '@mui/material/styles';
import {BLACK_100, WHITE_100} from '../../theme';
import Loading from '../../components/Loading';
import {useAddUsersToOrgMutation} from '../../data/api';
import PlusAddIcon from '../../assets/plus-add.svg';

/**
 * PSO specific modal shown when adding users that originate from a parent PSO org with SAML enabled
 */

const modalStyles = {
  base: {
    borderRadius: '0.5rem',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
    backgroundColor: WHITE_100,
  },
  title: {
    fontSize: '1.125rem',
    fontWeight: '700',
    lineHeight: '120%',
    color: '#043B34',
  },
  divider: {
    borderBottomWidth: '0.0625rem',
    opacity: 1,
    color: '#DDe0DE',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#043B34',
    marginBottom: '8px',
  },
  confirmButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingVertical: '0.625rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: '#26A993',
    },
  },
  confirmButtonLabel: {
    fontSize: '0.875rem',
    lineHeight: '140%',
    fontWeight: '500',
  },
  cancelButton: {
    borderRadius: '0.5rem',
    paddingVertical: '0.6825rem',
    paddingHorizontal: '1rem',
    height: '2.5rem',
    '&:hover': {
      backgroundColor: WHITE_100,
    },
  },
  cancelButtonLabel: {
    color: '#043B34',
    fontSize: '0.875rem',
    fontWeight: '500',
    lineHeight: '130%',
  },
  inputField: {
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      padding: '12px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      height: '42px',
      backgroundColor: '#fff',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused': {
        borderColor: '#26A993',
        backgroundColor: '#fff',
      },
      '&:hover': {
        borderColor: '#26A993',
      },
    },
    '& .MuiInputBase-input': {
      padding: '0',
      fontSize: '14px',
      fontWeight: 500,
      color: '#162523',
      '&::placeholder': {
        color: '#9AA7A6',
        opacity: 1,
      },
      '&::-webkit-search-cancel-button': {
        display: 'none',
      },
      '&::-webkit-search-decoration': {
        display: 'none',
      },
    },
    '& .MuiAutocomplete-popupIndicator': {
      display: 'none',
    },
    '& .MuiAutocomplete-clearIndicator': {
      marginRight: '8px',
    },
  },
  roleSelect: {
    '& .MuiOutlinedInput-root': {
      display: 'flex',
      padding: '12px 16px',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      borderRadius: '8px',
      border: '1px solid #E1E5E4',
      height: '42px',
      backgroundColor: '#fff',
      '& fieldset': {
        border: 'none',
      },
      '&:hover fieldset': {
        border: 'none',
      },
      '&.Mui-focused fieldset': {
        border: 'none',
      },
      '&.Mui-focused': {
        borderColor: '#26A993',
        backgroundColor: '#fff',
      },
      '&:hover': {
        borderColor: '#26A993',
      },
    },
    '& .MuiSelect-select': {
      padding: '0',
      fontSize: '14px',
      fontWeight: 500,
      color: '#162523',
    },
  },
};

const POSSIBLE_USER_ROLES = ['Admin', 'Reviewer', 'Report Preparer'];

const AddUserModal = ({
  open,
  onClose,
  orgId,
  orgName,
  onShowSnackbar,
  onUserAdded,
}) => {
  const [addUsersToOrg] = useAddUsersToOrgMutation();
  const {fetchOrgMembers} = useAuthFrontendApis();
  const {parentOrg} = useSelector((state) => state.session);
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([
    {
      id: 0,
      selectedUser: null,
      userRole: POSSIBLE_USER_ROLES[0],
      searchInput: '',
      searchResults: [],
      searchLoading: false,
    },
  ]);
  const [nextId, setNextId] = useState(1);
  const [currentOrgUserIds, setCurrentOrgUserIds] = useState([]);
  const [currentOrgUsersFetched, setCurrentOrgUsersFetched] = useState(false);
  const searchTimersRef = useRef(new Map());

  // Search parent org users via API
  const searchParentOrgUsers = useCallback(
    async (searchTerm, userIndex) => {
      if (!parentOrg || !searchTerm.trim()) {
        setUsers((prev) =>
          prev.map((user, index) =>
            index === userIndex
              ? {...user, searchResults: [], searchLoading: false}
              : user
          )
        );
        return;
      }

      setUsers((prev) =>
        prev.map((user, index) =>
          index === userIndex ? {...user, searchLoading: true} : user
        )
      );

      try {
        const parentResponse = await fetchOrgMembers(parentOrg.orgId, {
          page_number: 0,
          page_size: 10,
          email_search: searchTerm.trim(),
        });

        let parentUsers = [];

        await parentResponse.handle({
          success: (data) => {
            parentUsers =
              data.users?.map((user) => ({
                id: user.user_id,
                email: user.email,
                name: user.email.split('@')[0],
              })) || [];
          },
          orgNotFound: () => {
            parentUsers = [];
          },
          orgsNotEnabled: () => {
            parentUsers = [];
          },
          unauthorized: () => {
            parentUsers = [];
          },
          unexpectedOrUnhandled: () => {
            parentUsers = [];
          },
        });

        // Filter out users who are already members of the current org using cached IDs
        const filteredUsers = parentUsers.filter(
          (user) => !currentOrgUserIds.includes(user.id)
        );

        setUsers((prev) =>
          prev.map((user, index) =>
            index === userIndex
              ? {...user, searchResults: filteredUsers, searchLoading: false}
              : user
          )
        );
      } catch (error) {
        setUsers((prev) =>
          prev.map((user, index) =>
            index === userIndex
              ? {...user, searchResults: [], searchLoading: false}
              : user
          )
        );
      }
    },
    [parentOrg, fetchOrgMembers, currentOrgUserIds]
  );

  // Fetch current org members once when modal opens
  useEffect(() => {
    if (open && !currentOrgUsersFetched) {
      const fetchCurrentOrgMembers = async () => {
        try {
          const currentResponse = await fetchOrgMembers(orgId, {
            page_number: 0,
            page_size: 100,
            email_search: '',
          });

          await currentResponse.handle({
            success: (data) => {
              const userIds = data.users?.map((user) => user.user_id) || [];
              setCurrentOrgUserIds(userIds);
              setCurrentOrgUsersFetched(true);
            },
            orgNotFound: () => {
              setCurrentOrgUserIds([]);
              setCurrentOrgUsersFetched(true);
            },
            orgsNotEnabled: () => {
              setCurrentOrgUserIds([]);
              setCurrentOrgUsersFetched(true);
            },
            unauthorized: () => {
              setCurrentOrgUserIds([]);
              setCurrentOrgUsersFetched(true);
            },
            unexpectedOrUnhandled: () => {
              setCurrentOrgUserIds([]);
              setCurrentOrgUsersFetched(true);
            },
          });
        } catch (error) {
          setCurrentOrgUserIds([]);
          setCurrentOrgUsersFetched(true);
        }
      };

      fetchCurrentOrgMembers();
    }
  }, [open, currentOrgUsersFetched, fetchOrgMembers, orgId]);

  useEffect(() => {
    if (!open) {
      setUsers([
        {
          id: 0,
          selectedUser: null,
          userRole: POSSIBLE_USER_ROLES[0],
          searchInput: '',
          searchResults: [],
          searchLoading: false,
        },
      ]);
      setNextId(1);
      setCurrentOrgUserIds([]);
      setCurrentOrgUsersFetched(false);
    }
  }, [open]);

  // Debounced search function that can be called directly
  const debouncedSearch = useCallback(
    (searchInput, userIndex) => {
      // Clear existing timer for this user index
      if (searchTimersRef.current.has(userIndex)) {
        clearTimeout(searchTimersRef.current.get(userIndex));
      }

      // Set new timer for this user index
      const timer = setTimeout(() => {
        if (searchInput.trim()) {
          searchParentOrgUsers(searchInput, userIndex);
        } else {
          // Clear results if search input is empty
          setUsers((prev) =>
            prev.map((u, i) =>
              i === userIndex
                ? {...u, searchResults: [], searchLoading: false}
                : u
            )
          );
        }
      }, 300);

      searchTimersRef.current.set(userIndex, timer);
    },
    [searchParentOrgUsers]
  );

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      searchTimersRef.current.forEach((timer) => clearTimeout(timer));
      searchTimersRef.current.clear();
    };
  }, []);

  const addMoreUsers = () => {
    setUsers((prev) => [
      ...prev,
      {
        id: nextId,
        selectedUser: null,
        userRole: POSSIBLE_USER_ROLES[0],
        searchInput: '',
        searchResults: [],
        searchLoading: false,
      },
    ]);
    setNextId((prev) => prev + 1);
  };

  const updateUser = (index, updates) => {
    setUsers((prev) =>
      prev.map((user, i) => (i === index ? {...user, ...updates} : user))
    );
  };

  const removeUser = (index) => {
    if (users.length > 1) {
      setUsers((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleAdd = async () => {
    const validUsers = users.filter((user) => user.selectedUser);
    if (validUsers.length === 0) return;

    setIsLoading(true);

    try {
      // Send all users in a single API call
      const usersToAdd = validUsers.map((user) => ({
        userId: user.selectedUser.id,
        role: user.userRole,
      }));

      const result = await addUsersToOrg({
        orgId,
        users: usersToAdd,
      }).unwrap();

      // Check results and show appropriate messages
      const successful = result.results?.filter((r) => r.success) || [];
      const failed = result.results?.filter((r) => !r.success) || [];

      if (successful.length > 0) {
        const userCount = successful.length;
        const message =
          userCount === 1
            ? `User added successfully`
            : `${userCount} users added successfully`;

        onShowSnackbar({
          message,
          severity: 'success',
        });
      }

      if (failed.length > 0) {
        onShowSnackbar({
          message: `Failed to add ${failed.length} user(s)`,
          severity: 'error',
        });
      }

      // Call the callback to refresh the members list
      if (onUserAdded && successful.length > 0) {
        onUserAdded();
      }

      // Reset form and close modal if all successful
      if (failed.length === 0) {
        setUsers([
          {
            id: 0,
            selectedUser: null,
            userRole: POSSIBLE_USER_ROLES[0],
            searchInput: '',
            searchResults: [],
            searchLoading: false,
          },
        ]);
        setNextId(1);
        onClose();
      }
    } catch (error) {
      console.error('Failed to add users:', error);
      onShowSnackbar({
        message:
          error?.data?.message || error?.message || 'Failed to add users',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setUsers([
        {
          id: 0,
          selectedUser: null,
          userRole: POSSIBLE_USER_ROLES[0],
          searchInput: '',
          searchResults: [],
          searchLoading: false,
        },
      ]);
      setNextId(1);
      onClose();
    }
  };

  const validUserCount = users.filter((user) => user.selectedUser).length;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        position="absolute"
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width="40rem" minWidth="40rem" maxWidth="40rem">
          <Stack direction="column" sx={modalStyles.base}>
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography sx={modalStyles.title}>
                Add users to {orgName}
              </Typography>
              <IconButton onClick={handleClose} disabled={isLoading}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="column"
              paddingTop="1.25rem"
              paddingBottom="1.5rem"
              paddingX="1.5rem"
              spacing="1rem"
              sx={{
                maxHeight: '40vh',
                overflowY: 'auto',
              }}
            >
              {/* User Rows */}
              {users.map((user, index) => (
                <Box key={user.id}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    {/* Email Search */}
                    <Stack direction="column" spacing={1} flex={1}>
                      <Typography sx={modalStyles.label}>Email</Typography>
                      <Autocomplete
                        options={user.searchResults}
                        getOptionLabel={(option) => option?.email || ''}
                        isOptionEqualToValue={(option, value) =>
                          option?.id === value?.id
                        }
                        value={user.selectedUser}
                        onChange={(event, newValue) => {
                          updateUser(index, {
                            selectedUser: newValue,
                            searchInput: newValue?.email || '',
                          });
                        }}
                        inputValue={user.searchInput}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input') {
                            updateUser(index, {searchInput: newInputValue});
                            debouncedSearch(newInputValue, index);
                          } else if (reason === 'reset' && user.selectedUser) {
                            updateUser(index, {
                              searchInput: user.selectedUser.email,
                            });
                          }
                        }}
                        loading={user.searchLoading}
                        disabled={isLoading}
                        autoComplete
                        includeInputInList
                        filterOptions={(options, params) => {
                          if (!params.inputValue.trim()) return [];
                          return options.filter((option) =>
                            option.email
                              .toLowerCase()
                              .includes(params.inputValue.toLowerCase())
                          );
                        }}
                        noOptionsText={
                          user.searchLoading
                            ? 'Searching...'
                            : user.searchInput.trim()
                              ? 'No users found'
                              : 'Start typing to search users'
                        }
                        sx={modalStyles.inputField}
                        componentsProps={{
                          paper: {
                            sx: {
                              borderRadius: '8px',
                              border: '1px solid #E1E5E4',
                              boxShadow: 'none',
                              '& .MuiAutocomplete-listbox': {
                                '& .MuiAutocomplete-option': {
                                  color: '#162523',
                                  fontSize: '14px',
                                  fontStyle: 'normal',
                                  fontWeight: 500,
                                  lineHeight: '130%',
                                  letterSpacing: '-0.14px',
                                },
                              },
                              '& .MuiAutocomplete-noOptions': {
                                color: '#162523',
                                fontSize: '14px',
                                fontStyle: 'normal',
                                fontWeight: 500,
                                lineHeight: '130%',
                                letterSpacing: '-0.14px',
                              },
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="jane@pwc.com"
                            variant="outlined"
                            autoFocus={index === 0}
                            inputProps={{
                              ...params.inputProps,
                              autoComplete: 'new-password',
                              'data-1p-ignore': true,
                              'data-lpignore': true,
                              'data-form-type': 'other',
                              name: `user-search-${index}`,
                              type: 'search',
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {user.searchLoading ? (
                                    <CircularProgress
                                      color="inherit"
                                      size={20}
                                    />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                                color: '#162523',
                              }}
                            >
                              {option.email}
                            </Typography>
                          </Box>
                        )}
                      />
                    </Stack>

                    {/* Role Selector */}
                    <Stack direction="column" spacing={1} flex={1}>
                      <Typography sx={modalStyles.label}>Role</Typography>
                      <FormControl fullWidth sx={modalStyles.roleSelect}>
                        <Select
                          value={user.userRole}
                          onChange={(event) =>
                            updateUser(index, {userRole: event.target.value})
                          }
                          disabled={isLoading}
                        >
                          {POSSIBLE_USER_ROLES.map((role) => (
                            <MenuItem key={role} value={role}>
                              {role}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>

                    {/* Remove Button - only show if more than one user */}
                    {users.length > 1 && (
                      <Stack direction="column" spacing={1} alignItems="center">
                        <Typography sx={{...modalStyles.label, opacity: 0}}>
                          &nbsp;
                        </Typography>
                        <IconButton
                          onClick={() => removeUser(index)}
                          disabled={isLoading}
                          sx={{
                            padding: '9px',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <CircleMinus size={24} color="#000000" />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                </Box>
              ))}

              {/* Add More Users Button */}
              <Box sx={{display: 'flex', justifyContent: 'flex-start'}}>
                <Button
                  variant="text"
                  startIcon={
                    <img src={PlusAddIcon} alt="Add" width={16} height={16} />
                  }
                  onClick={addMoreUsers}
                  disabled={isLoading}
                  sx={{
                    color: '#26A993',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    padding: '8px 12px',
                    justifyContent: 'flex-start',
                    width: 'auto',
                    minWidth: 'auto',
                    '&:hover': {
                      backgroundColor: 'rgba(38, 169, 147, 0.04)',
                    },
                  }}
                >
                  Add more users
                </Button>
              </Box>
            </Stack>
            <Divider orientation="horizontal" sx={modalStyles.divider} />
            <Stack
              direction="row"
              paddingY="1rem"
              paddingLeft="1.5rem"
              paddingRight="1rem"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              spacing="0.5rem"
            >
              <Button
                variant="outlined"
                sx={modalStyles.cancelButton}
                onClick={handleClose}
                disabled={isLoading}
              >
                <Typography style={modalStyles.cancelButtonLabel}>
                  Cancel
                </Typography>
              </Button>
              <LoadingButton
                disabled={validUserCount === 0 || isLoading}
                variant="contained"
                sx={{
                  ...theme.button.primary.large,
                  '&.Mui-disabled': {
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    color: '#fff',
                    backgroundColor: '#34A290',
                  },
                }}
                onClick={handleAdd}
                loading={isLoading}
                loadingIndicator={
                  <Loading
                    loading={isLoading}
                    loadingProps={{color: BLACK_100}}
                  />
                }
              >
                Add users
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

AddUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  orgName: PropTypes.string.isRequired,
  onShowSnackbar: PropTypes.func.isRequired,
  onUserAdded: PropTypes.func,
};

export default AddUserModal;
